import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useAnimation, useInView } from 'framer-motion';
import { FaDesktop, FaMobileAlt, FaShoppingCart, FaUniversalAccess, FaPencilRuler, 
         FaArrowRight, FaStar, FaCheck, FaLightbulb, FaEye, FaCode, FaPalette, 
         FaMagic, FaCog, FaAccessibleIcon, FaRegLightbulb } from 'react-icons/fa';
import { Helmet } from 'react-helmet';

// Throttle function for performance optimization
const throttle = (func, limit) => {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  }
};

const TechEcoSolutions = () => {
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [activeSection, setActiveSection] = useState('website');
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeBulbs, setActiveBulbs] = useState([]);
  const heroRef = useRef(null);
  const workSectionRef = useRef(null);
  const controls = useAnimation();
  const isWorkInView = useInView(workSectionRef);

  // Structured Data Schema
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": "https://techecosolution.in/ui-ux-design-services",
        "url": "https://techecosolution.in/ui-ux-design-services",
        "name": "UI/UX Design Services - Expert Digital Experience Solutions",
        "description": "Professional UI/UX design services specializing in website design, e-commerce interfaces, accessibility, and wireframing. Transform your digital experience today.",
        "inLanguage": "en-US",
        "isPartOf": {
          "@type": "WebSite",
          "@id": "https://techecosolution.in",
          "url": "https://techecosolution.in",
          "name": "Techecosolution",
          "publisher": {
            "@type": "Organization",
            "@id": "https://techecosolution.in#organization"
          }
        },
        "primaryImageOfPage": {
          "@type": "ImageObject",
          "url": "https://techecosolution.in/images/uiux-services-banner.jpg",
          "width": 1200,
          "height": 630
        },
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://techecosolution.in"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://techecosolution.in/services"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "UI/UX Design",
              "item": "https://techecosolution.in/ui-ux-design-services"
            }
          ]
        }
      },
      {
        "@type": "Organization",
        "@id": "https://techecosolution.in#organization",
        "name": "Techecosolution",
        "url": "https://techecosolution.in",
        "logo": {
          "@type": "ImageObject",
          "url": "https://techecosolution.in/logo.png",
          "width": 300,
          "height": 100
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+91-9483085077",
          "contactType": "customer service",
          "areaServed": "IN",
          "availableLanguage": "English"
        },
        "sameAs": [
          "https://linkedin.com/company/techecosolution",
          "https://twitter.com/techecosolution"
        ],
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "IN",
          "addressRegion": "Your State",
          "addressLocality": "Your City"
        }
      },
      {
        "@type": "Service",
        "serviceType": "UI/UX Design Services",
        "provider": {
          "@type": "Organization",
          "@id": "https://techecosolution.in"
        },
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "UI/UX Design Services",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Website UI/UX Design",
                "description": "Create intuitive, user-friendly layouts that align with your audience's needs and behaviors."
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Wireframing & Prototyping",
                "description": "Create structured wireframes that outline website/app layout and functionality."
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "E-Commerce UI/UX",
                "description": "Design intuitive, conversion-focused interfaces that enhance customer engagement."
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Accessibility Design",
                "description": "Ensure universal accessibility with designs optimized for all users."
              }
            }
          ]
        }
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "What UI/UX design services do you offer?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "We offer comprehensive UI/UX design services including website design, e-commerce interfaces, wireframing & prototyping, accessibility design, mobile-first responsive design, and user experience optimization."
            }
          },
          {
            "@type": "Question",
            "name": "How do you ensure accessibility in your designs?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "We follow WCAG and ADA compliance guidelines, implement keyboard navigation, screen reader support, enhanced color contrast, and conduct accessibility testing to ensure inclusive designs for all users."
            }
          },
          {
            "@type": "Question",
            "name": "What is your design process?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Our design process includes user research, wireframing, prototyping, visual design, user testing, and iterative improvements based on feedback and analytics to ensure optimal user experience."
            }
          },
          {
            "@type": "Question",
            "name": "Do you provide mobile-responsive designs?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, all our designs follow a mobile-first approach and are fully responsive across all devices and screen sizes to ensure seamless user experiences."
            }
          },
          {
            "@type": "Question",
            "name": "How do you measure design success?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "We measure design success through user engagement metrics, conversion rates, usability testing results, accessibility compliance, and continuous user feedback analysis."
            }
          }
        ]
      }
    ]
  };

  // Optimized cursor tracking with throttling
  useEffect(() => {
    const handleMouseMove = throttle((e) => {
      setCursorPosition({ x: e.clientX, y: e.clientY });
    }, 16); // 60fps throttling

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Automatically animate falling bulbs (heavily optimized)
  useEffect(() => {
    let interval;
    
    if (isWorkInView) {
      interval = setInterval(() => {
        const randomBulb = Math.floor(Math.random() * 3); // Reduced from 6 to 3
        setActiveBulbs(prev => {
          if (prev.includes(randomBulb)) {
            return prev;
          }
          return [...prev, randomBulb];
        });

        setTimeout(() => {
          setActiveBulbs(prev => prev.filter(bulb => bulb !== randomBulb));
        }, 3000);
      }, 2000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isWorkInView]); 

  // Core Web Vitals tracking
  useEffect(() => {
    if (typeof window !== 'undefined') {
      import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
        getCLS(console.log);
        getFID(console.log);
        getFCP(console.log);
        getLCP(console.log);
        getTTFB(console.log);
      }).catch(() => {
        // Handle import error silently
      });
    }
  }, []);

  const services = {
    website: {
      title: "Website UI/UX Design",
      subtitle: "Intuitive & Engaging User Experiences",
      description: "Create intuitive, user-friendly layouts that align with your audience's needs and behaviors.",
      image: "https://images.unsplash.com/photo-1581291518633-83b4ebd1d83e?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80",
      icon: <FaDesktop className="text-4xl" />,
      color: "from-blue-500 to-purple-600",
      features: [
        "User-Centered Design",
        "Custom Layouts & Branding",
        "Fully Responsive Design",
        "Simplified Navigation & UX Flow",
        "Engaging Interactive Elements",
        "Optimized for Speed & Performance",
        "Conversion-Focused UI/UX",
        "Wireframing & Prototyping",
        "User Testing & Feedback Integration"
      ]
    },
    wireframing: {
      title: "Wireframing & Prototyping",
      subtitle: "Blueprint for a Seamless Design",
      description: "Create structured wireframes that outline website/app layout and functionality.",
      image: "https://images.unsplash.com/photo-1561070791-2526d30994b5?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80",
      icon: <FaPencilRuler className="text-4xl" />,
      color: "from-green-500 to-teal-600",
      features: [
        "Visual Blueprint for Development",
        "Interactive Prototypes for Real-Time Testing",
        "User-Centric Design Approach",
        "Quick Iterations & Adaptability",
        "Effective Stakeholder Communication",
        "Cost-Effective & Time-Saving",
        "Optimized for Desktop & Mobile",
        "Low-Fidelity & High-Fidelity Designs"
      ]
    },
    ecommerce: {
      title: "E-Commerce UI/UX",
      subtitle: "Optimize for Higher Conversions",
      description: "Design intuitive, conversion-focused interfaces that enhance customer engagement.",
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80",
      icon: <FaShoppingCart className="text-4xl" />,
      color: "from-orange-500 to-red-600",
      features: [
        "Customer-Centric Shopping Experience",
        "Streamlined Checkout Process",
        "Mobile-Optimized Shopping",
        "Product-Focused Layouts",
        "Advanced Search & Filtering",
        "Simplified & Intuitive Navigation",
        "Interactive Shopping Elements",
        "Fast Loading Speed",
        "Security & Trust Elements"
      ]
    },
    accessibility: {
      title: "Accessibility Design",
      subtitle: "Inclusive & Compliant Digital Solutions",
      description: "Ensure universal accessibility with designs optimized for all users.",
      image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80",
      icon: <FaUniversalAccess className="text-4xl" />,
      color: "from-pink-500 to-purple-600",
      features: [
        "Inclusive User Experience",
        "WCAG & ADA Compliance",
        "Keyboard & Screen Reader Support",
        "Enhanced Color Contrast & Readability",
        "Adaptive & Responsive Design",
        "Accessible Forms & Buttons",
        "Audio & Visual Support",
        "Error Prevention & Guidance"
      ]
    }
  };

  const activeData = services[activeSection];

  const portfolioItems = [
    {
      title: "E-Commerce Redesign",
      description: "Conversion-focused interface with intuitive navigation",
      image: "https://images.unsplash.com/photo-1607082349566-187342175e2f?ixlib=rb-1.2.1&auto=format&fit=crop&w=2000&q=80",
      color: "from-pink-500 to-red-500"
    },
    {
      title: "Banking App Interface",
      description: "Secure and accessible financial management",
      image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?ixlib=rb-1.2.1&auto=format&fit=crop&w=2000&q=80",
      color: "from-blue-500 to-indigo-500"
    },
    {
      title: "Healthcare Portal",
      description: "Patient-centered design with clear information hierarchy",
      image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-1.2.1&auto=format&fit=crop&w=2000&q=80",
      color: "from-green-500 to-teal-500"
    },
    {
      title: "Travel Booking Platform",
      description: "Immersive experience with seamless booking flow",
      image: "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?ixlib=rb-1.2.1&auto=format&fit=crop&w=2000&q=80",
      color: "from-yellow-500 to-orange-500"
    },
    {
      title: "Fitness Tracking App",
      description: "Motivational UI with progress visualization",
      image: "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?ixlib=rb-1.2.1&auto=format&fit=crop&w=2000&q=80",
      color: "from-purple-500 to-violet-500"
    },
    {
      title: "Smart Home Dashboard",
      description: "Intuitive control center for connected devices",
      image: "https://images.unsplash.com/photo-1558002038-bb0237f4476b?ixlib=rb-1.2.1&auto=format&fit=crop&w=2000&q=80",
      color: "from-cyan-500 to-blue-500"
    }
  ];

  const features = [
    {
      icon: <FaDesktop />,
      title: "User-Centric Design",
      description: "Prioritizing usability, engagement, and aesthetics for optimal user experience.",
      color: "from-blue-500 to-purple-600"
    },
    {
      icon: <FaMobileAlt />,
      title: "Mobile-First Approach",
      description: "Optimized for all devices and screen sizes to ensure seamless experiences.",
      color: "from-green-500 to-teal-600"
    },
    {
      icon: <FaShoppingCart />,
      title: "High-Converting Layouts",
      description: "Designed to increase engagement and drive conversions for better results.",
      color: "from-orange-500 to-red-600"
    },
    {
      icon: <FaEye />,
      title: "Data-Driven Testing",
      description: "Ensure smooth functionality with real-time user feedback and analytics.",
      color: "from-pink-500 to-purple-600"
    },
    {
      icon: <FaPalette />,
      title: "Custom Designs",
      description: "Every project is tailored to your brand and specific business needs.",
      color: "from-indigo-500 to-blue-600"
    },
    {
      icon: <FaAccessibleIcon />,
      title: "Accessibility Focused",
      description: "Creating inclusive designs that work for everyone, regardless of ability.",
      color: "from-yellow-500 to-amber-600"
    }
  ];

  const faqs = [
    {
      question: "What UI/UX design services do you offer?",
      answer: "We offer comprehensive UI/UX design services including website design, e-commerce interfaces, wireframing & prototyping, accessibility design, mobile-first responsive design, and user experience optimization."
    },
    {
      question: "How do you ensure accessibility in your designs?",
      answer: "We follow WCAG and ADA compliance guidelines, implement keyboard navigation, screen reader support, enhanced color contrast, and conduct accessibility testing to ensure inclusive designs for all users."
    },
    {
      question: "What is your design process?",
      answer: "Our design process includes user research, wireframing, prototyping, visual design, user testing, and iterative improvements based on feedback and analytics to ensure optimal user experience."
    },
    {
      question: "Do you provide mobile-responsive designs?",
      answer: "Yes, all our designs follow a mobile-first approach and are fully responsive across all devices and screen sizes to ensure seamless user experiences."
    },
    {
      question: "How do you measure design success?",
      answer: "We measure design success through user engagement metrics, conversion rates, usability testing results, accessibility compliance, and continuous user feedback analysis."
    }
  ];

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>UI/UX Design Services - Expert Digital Experience Solutions | Techecosolution</title>
        <meta name="description" content="Professional UI/UX design services specializing in website design, e-commerce interfaces, accessibility, and wireframing. Transform your digital experience today." />
        <meta name="keywords" content="UI UX design, website design services, e-commerce UI design, accessibility design, wireframing prototyping, user experience design, digital design agency" />
        
        {/* Geographic Targeting */}
        <meta name="geo.region" content="IN" />
        <meta name="geo.placename" content="India" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
        
        {/* Open Graph Meta Tags */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="UI/UX Design Services - Expert Digital Experience Solutions" />
        <meta property="og:description" content="Professional UI/UX design services specializing in website design, e-commerce interfaces, accessibility, and wireframing. Transform your digital experience today." />
        <meta property="og:image" content="https://www.techecosolution.in/images/uiux-services-banner.jpg" />
        <meta property="og:url" content="https://www.techecosolution.in/ui-ux-design-services" />
        <meta property="og:site_name" content="Techecosolution" />
        
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="UI/UX Design Services - Expert Digital Experience Solutions" />
        <meta name="twitter:description" content="Professional UI/UX design services specializing in website design, e-commerce interfaces, accessibility, and wireframing." />
        <meta name="twitter:image" content="https://www.techecosolution.in/images/uiux-services-banner.jpg" />

        {/* Canonical URL */}
        <link rel="canonical" href="https://www.techecosolution.in/ui-ux-design-services" />
        
        {/* Preconnect for Performance */}
        <link rel="preconnect" href="https://images.unsplash.com" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        
        {/* Language */}
        <html lang="en" />
      </Helmet>

      {/* Structured Data */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      <main role="main" className="min-h-screen bg-black text-white overflow-hidden">
        {/* Background Elements */}
        <div className="fixed inset-0 z-0">
          {/* Grid pattern */}
          <div className="absolute inset-0 opacity-10">
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <defs>
                <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="0.5"/>
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>
          </div>

          {/* Animated circles - minimal count */}
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={`circle-${i}`}
              className="absolute rounded-full border border-purple-500/20"
              style={{
                width: Math.random() * 300 + 100,
                height: Math.random() * 300 + 100,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                opacity: Math.random() * 0.2 + 0.1,
              }}
              animate={{
                scale: [1, 1.05, 1], // Reduced scale range
                rotate: 180, // Reduced rotation
                opacity: [0.1, 0.15, 0.1] // Reduced opacity range
              }}
              transition={{
                duration: Math.random() * 30 + 30, // Much slower animation
                repeat: Infinity,
                ease: "linear"
              }}
              aria-hidden="true"
            />
          ))}

          {/* Vector lines - further reduced */}
          {[...Array(4)].map((_, i) => (
            <motion.div
              key={`line-${i}`}
              className="absolute bg-gradient-to-r from-purple-500/0 via-purple-500/20 to-purple-500/0"
              style={{
                height: '1px',
                width: '100%',
                top: `${Math.random() * 100}%`,
                left: 0,
              }}
              animate={{
                opacity: [0.1, 0.3, 0.1],
                scaleY: [1, 1.5, 1]
              }}
              transition={{
                duration: Math.random() * 10 + 10,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              aria-hidden="true"
            />
          ))}
        </div>

        {/* Custom cursor effect */}
        <div 
          className="pointer-events-none fixed w-80 h-80 rounded-full blur-[100px] opacity-30 z-10 mix-blend-screen"
          style={{
            background: `radial-gradient(circle, rgba(147, 51, 234, 0.7) 0%, rgba(79, 70, 229, 0.7) 100%)`,
            left: cursorPosition.x - 150,
            top: cursorPosition.y - 150,
            transition: 'transform 0.1s ease-out',
          }}
          aria-hidden="true"
        />

        {/* Hero section with 3D cards */}
        <header ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden pb-32 md:pb-20">
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-gradient-radial from-purple-900/20 via-black to-black"></div>
          </div>

          {/* Floating vector elements - minimal count */}
          <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
            {[...Array(4)].map((_, i) => (
              <motion.div
                key={`vector-${i}`}
                className="absolute"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{
                  y: [0, Math.random() * 50 - 25],
                  x: [0, Math.random() * 50 - 25],
                  rotate: [0, Math.random() * 180 - 90],
                }}
                transition={{
                  duration: Math.random() * 25 + 30, // Much slower animation
                  repeat: Infinity,
                  repeatType: "reverse",
                  ease: "easeInOut"
                }}
              >
                <svg width={Math.random() * 30 + 20} height={Math.random() * 30 + 20} viewBox="0 0 100 100" aria-hidden="true">
                  <motion.path
                    d={[
                      "M50,10 L90,50 L50,90 L10,50 Z",
                      "M20,20 L80,20 L80,80 L20,80 Z",
                      "M50,10 A40,40 0 1,0 50,90 A40,40 0 1,0 50,10 Z"
                    ][Math.floor(Math.random() * 3)]}
                    stroke={`rgba(${Math.random() * 100 + 155}, ${Math.random() * 100 + 100}, ${Math.random() * 255}, ${Math.random() * 0.3 + 0.1})`}
                    strokeWidth="2"
                    fill="none"
                  />
                </svg>
              </motion.div>
            ))}
          </div>

          <div className="container mx-auto px-4 z-10 relative">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1 }}
              className="text-center max-w-5xl mx-auto"
            >
              <motion.div 
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.8 }}
                className="mb-8 relative inline-block"
              >
                <div className="relative z-10">
                  <FaRegLightbulb className="text-6xl md:text-8xl text-yellow-300 mx-auto" aria-hidden="true" />
                </div>
                <motion.div 
                  className="absolute -inset-10 rounded-full blur-xl z-0"
                  animate={{ 
                    scale: [1, 1.2, 1],
                  }}
                  transition={{ 
                    repeat: Infinity,
                    duration: 3
                  }}
                  style={{
                    background: 'radial-gradient(circle, rgba(253, 224, 71, 0.3) 0%, rgba(253, 224, 71, 0) 70%)',
                  }}
                  aria-hidden="true"
                />
              </motion.div>
              
              <motion.h1 
                className="text-4xl md:text-7xl font-extrabold mb-6 leading-tight"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.8 }}
              >
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-pink-500 to-red-500">
                  Expert UI/UX Design Services
                </span>
              </motion.h1>
              
              <motion.p 
                className="text-lg md:text-2xl mb-10 text-gray-300 max-w-3xl mx-auto"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.8 }}
              >
                Transform your digital presence with professional UI/UX design services that enhance user engagement, improve website usability, and drive business conversions through data-driven design strategies.
              </motion.p>
            </motion.div>
          </div>

          {/* 3D floating cards - Responsive positioning */}
          <div className="absolute bottom-4 md:bottom-20 left-0 right-0 overflow-hidden pointer-events-none">
            <div className="flex flex-wrap justify-center gap-2 md:gap-6 px-4">
              {Object.keys(services).map((key, index) => (
                <motion.div
                  key={key}
                  className="w-40 h-16 md:w-60 md:h-24 bg-black/50 backdrop-blur-md rounded-xl border border-purple-500/30 flex items-center justify-center px-2 md:px-4"
                  initial={{ y: 100, opacity: 0 }}
                  animate={{ 
                    y: 0, 
                    opacity: 1,
                    rotateX: [0, 10, 0, -10, 0],
                    rotateY: [0, -10, 0, 10, 0],
                  }}
                  transition={{
                    delay: 1 + index * 0.2,
                    duration: 0.8,
                    rotateX: {
                      repeat: Infinity,
                      duration: 6,
                      delay: index * 1.5
                    },
                    rotateY: {
                      repeat: Infinity,
                      duration: 6,
                      delay: index * 1.5
                    }
                  }}
                  aria-hidden="true"
                >
                  <div className="mr-2 md:mr-3 text-sm md:text-base">{services[key].icon}</div>
                  <p className="font-medium text-xs md:text-base text-center leading-tight">{services[key].title}</p>
                  
                  {/* Neon glow border */}
                  <motion.div 
                    className="absolute -inset-px rounded-xl opacity-0"
                    animate={{ opacity: [0, 0.5, 0] }}
                    transition={{ repeat: Infinity, duration: 2, delay: index * 0.5 }}
                    style={{
                      background: `linear-gradient(90deg, transparent, ${key === 'website' ? '#8B5CF6' : key === 'wireframing' ? '#10B981' : key === 'ecommerce' ? '#F97316' : '#EC4899'}, transparent)`,
                      filter: 'blur(4px)',
                    }}
                  />
                </motion.div>
              ))}
            </div>
          </div>

          {/* Animated circles at the bottom */}
          <div className="absolute bottom-0 left-0 right-0 h-40 overflow-hidden" aria-hidden="true">
            {[...Array(5)].map((_, i) => (
              <motion.div
                key={`bottom-circle-${i}`}
                className="absolute rounded-full bg-purple-500/10"
                style={{
                  width: Math.random() * 100 + 50,
                  height: Math.random() * 100 + 50,
                  left: `${Math.random() * 100}%`,
                  bottom: `-${Math.random() * 20}px`,
                }}
                animate={{
                  y: [0, -30, 0],
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.6, 0.3]
                }}
                transition={{
                  duration: Math.random() * 5 + 5,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
            ))}
          </div>
        </header>

        {/* Services section with card design */}
        <section aria-labelledby="services-heading" className="py-20 relative overflow-hidden">
          <div className="absolute inset-0 bg-black"></div>
          
          {/* Animated vector lines */}
          <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={`service-line-${i}`}
                className="absolute h-px bg-gradient-to-r from-purple-500/0 via-purple-500/30 to-purple-500/0"
                style={{
                  width: '100%',
                  top: `${(i + 1) * 100 / 7}%`,
                  left: 0,
                }}
                animate={{
                  scaleY: [1, 3, 1],
                  opacity: [0.1, 0.3, 0.1]
                }}
                transition={{
                  duration: 8,
                  repeat: Infinity,
                  delay: i * 1,
                  ease: "easeInOut"
                }}
              />
            ))}
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <motion.header
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
              className="text-center mb-16"
            >
              <h2 id="services-heading" className="text-4xl md:text-5xl font-bold mb-6">
                Our UI/UX Design Services
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                From concept to completion, we create designs that captivate users and drive business results through proven design methodologies.
              </p>
            </motion.header>

            {/* Service tabs with neon effect */}
            <nav className="flex flex-wrap justify-center gap-4 mb-16" role="tablist" aria-label="UI/UX Design Services">
              {Object.keys(services).map((key) => (
                <motion.button
                  key={key}
                  onClick={() => setActiveSection(key)}
                  role="tab"
                  aria-selected={activeSection === key}
                  aria-controls={`service-panel-${key}`}
                  className={`px-6 py-3 rounded-full text-lg font-medium transition-all duration-300 relative overflow-hidden ${
                    activeSection === key 
                    ? `bg-black border border-${key === 'website' ? 'purple' : key === 'wireframing' ? 'green' : key === 'ecommerce' ? 'orange' : 'pink'}-500` 
                    : 'bg-black border border-gray-800 hover:border-gray-700'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <div className="flex items-center relative z-10">
                    <span className="mr-2" aria-hidden="true">{services[key].icon}</span>
                    <span>{services[key].title}</span>
                  </div>
                  
                  {/* Neon glow effect */}
                  {activeSection === key && (
                    <motion.div 
                      className="absolute inset-0 opacity-20"
                      animate={{ 
                        boxShadow: ['0 0 5px #8B5CF6, 0 0 10px #8B5CF6', '0 0 15px #8B5CF6, 0 0 30px #8B5CF6', '0 0 5px #8B5CF6, 0 0 10px #8B5CF6'] 
                      }}
                      transition={{ 
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                      aria-hidden="true"
                    />
                  )}
                </motion.button>
              ))}
            </nav>

            {/* Active service content with card design */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeSection}
                id={`service-panel-${activeSection}`}
                role="tabpanel"
                aria-labelledby={`tab-${activeSection}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="relative"
              >
                <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-pink-600 rounded-3xl blur opacity-20" aria-hidden="true"></div>
                <motion.div 
                  className="relative bg-black border border-purple-500/30 rounded-3xl overflow-hidden"
                  whileHover={{ 
                    boxShadow: '0 0 30px rgba(139, 92, 246, 0.3)'
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="relative flex flex-col lg:flex-row">
                    {/* Image section */}
                    <div className="w-full lg:w-1/2 h-[300px] lg:h-auto relative">
                      <img 
                        src={activeData.image} 
                        alt={`${activeData.title} - Professional UI/UX design services demonstrating ${activeData.subtitle}`}
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="eager"
                        decoding="async"
                        width="600"
                        height="400"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent"></div>
                      
                      {/* Animated overlay elements */}
                      <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
                        {[...Array(4)].map((_, i) => (
                          <motion.div
                            key={`element-${i}`}
                            className="absolute w-1 h-1 rounded-full bg-purple-500"
                            style={{
                              left: `${Math.random() * 100}%`,
                              top: `${Math.random() * 100}%`,
                            }}
                            animate={{
                              scale: [0, 1, 0],
                              opacity: [0, 1, 0],
                            }}
                            transition={{
                              duration: Math.random() * 3 + 2,
                              repeat: Infinity,
                              delay: Math.random() * 5,
                              ease: "easeInOut"
                            }}
                          />
                        ))}
                      </div>
                      
                      {/* Service icon with glow */}
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                        <motion.div
                          className={`p-6 rounded-full bg-black border border-purple-500 relative`}
                          animate={{ 
                            boxShadow: ['0 0 10px rgba(139, 92, 246, 0.5)', '0 0 20px rgba(139, 92, 246, 0.8)', '0 0 10px rgba(139, 92, 246, 0.5)'] 
                          }}
                          transition={{ 
                            duration: 3,
                            repeat: Infinity,
                            ease: "easeInOut"
                          }}
                          aria-hidden="true"
                        >
                          <div className="text-5xl text-purple-500">{activeData.icon}</div>
                        </motion.div>
                      </div>
                    </div>
                    
                    {/* Content section */}
                    <div className="w-full lg:w-1/2 p-8 lg:p-12 flex flex-col justify-center">
                      <h3 className="text-3xl lg:text-4xl font-bold mb-2">
                        <span className={`bg-clip-text text-transparent bg-gradient-to-r ${activeData.color}`}>
                          {activeData.title}
                        </span>
                      </h3>
                      <h4 className="text-xl lg:text-2xl font-medium mb-6 text-gray-300">{activeData.subtitle}</h4>
                      <p className="text-lg mb-8">{activeData.description}</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {activeData.features.map((feature, index) => (
                          <motion.div 
                            key={index}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="flex items-center"
                          >
                            <motion.div 
                              className="mr-3 p-2 rounded-full bg-black border border-purple-500"
                              whileHover={{ scale: 1.2 }}
                              animate={{
                                boxShadow: ['0 0 0px rgba(139, 92, 246, 0.3)', '0 0 10px rgba(139, 92, 246, 0.6)', '0 0 0px rgba(139, 92, 246, 0.3)']
                              }}
                              transition={{
                                boxShadow: { repeat: Infinity, duration: 2 }
                              }}
                              aria-hidden="true"
                            >
                              <FaCheck className="text-purple-500" />
                            </motion.div>
                            <span>{feature}</span>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </AnimatePresence>
          </div>
        </section>

        {/* Work showcase with card design and auto-glowing bulbs */}
        <section ref={workSectionRef} aria-labelledby="portfolio-heading" className="py-20 relative overflow-hidden">
          <div className="absolute inset-0 bg-black"></div>
          
          {/* Animated background elements */}
          <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
            {/* Circular grid pattern */}
            <div className="absolute inset-0 opacity-10">
              <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <defs>
                  <pattern id="circles" width="80" height="80" patternUnits="userSpaceOnUse">
                    <circle cx="40" cy="40" r="30" fill="none" stroke="rgba(168, 85, 247, 0.3)" strokeWidth="0.5" />
                    <circle cx="40" cy="40" r="15" fill="none" stroke="rgba(168, 85, 247, 0.3)" strokeWidth="0.5" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#circles)" />
              </svg>
            </div>
            
            {/* Animated vector lines - reduced count */}
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={`work-line-${i}`}
                className="absolute w-px bg-gradient-to-b from-purple-500/0 via-purple-500/30 to-purple-500/0"
                style={{
                  height: '100%',
                  left: `${(i + 1) * 100 / 11}%`,
                  top: 0,
                }}
                animate={{
                  scaleX: [1, 3, 1],
                  opacity: [0.1, 0.3, 0.1]
                }}
                transition={{
                  duration: 8,
                  repeat: Infinity,
                  delay: i * 1,
                  ease: "easeInOut"
                }}
              />
            ))}
          </div>
          
          {/* Automatically falling and glowing bulbs - minimal count */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
            {[...Array(3)].map((_, i) => (
              <motion.div
                key={`bulb-${i}`}
                className="absolute"
                initial={{ 
                  top: -100, 
                  left: `${(i % 4) * 25 + Math.random() * 20}%`,
                  opacity: 0
                }}
                animate={activeBulbs.includes(i) ? {
                  top: '120%',
                  opacity: [0, 1, 1, 0],
                } : {}}
                transition={{
                  duration: 7, // Increased from 5 for slower animation
                  ease: "easeIn"
                }}
              >
                <div className="relative">
                  <FaLightbulb 
                    className={`text-4xl ${activeBulbs.includes(i) ? 'text-yellow-300' : 'text-gray-600'}`}
                  />
                  {activeBulbs.includes(i) && (
                    <motion.div 
                      className="absolute -inset-4 rounded-full blur-xl z-0"
                      style={{
                        background: 'radial-gradient(circle, rgba(253, 224, 71, 0.8) 0%, rgba(253, 224, 71, 0) 70%)',
                      }}
                    />
                  )}
                </div>
              </motion.div>
            ))}
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <motion.header
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
              className="text-center"
            >
              <h2 id="portfolio-heading" className="text-4xl md:text-5xl font-bold mb-2 relative inline-block">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-600">
                  Our Design Portfolio
                </span>
                {/* Decorative light bulb */}
                <motion.div 
                  className="absolute -top-16 left-1/2 transform -translate-x-1/2"
                  animate={{ y: [0, -5, 0] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                  aria-hidden="true"
                >
                  <FaLightbulb className="text-4xl text-yellow-300" />
                  <motion.div 
                    className="absolute -inset-4 rounded-full blur-xl z-0"
                    animate={{ opacity: [0.3, 0.7, 0.3], scale: [1, 1.2, 1] }}
                    transition={{ repeat: Infinity, duration: 3 }}
                    style={{
                      background: 'radial-gradient(circle, rgba(253, 224, 71, 0.6) 0%, rgba(253, 224, 71, 0) 70%)',
                    }}
                  />
                </motion.div>
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Explore our portfolio of innovative designs that deliver measurable results and exceptional user experiences.
              </p>
            </motion.header>
            
            {/* Work showcase with card design */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {portfolioItems.map((item, i) => (
                <motion.article
                  key={`project-${i}`}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  whileHover={{ 
                    y: -10,
                    transition: { duration: 0.3 }
                  }}
                  className="group relative rounded-xl overflow-hidden"
                >
                  {/* Glowing border */}
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl blur opacity-0 group-hover:opacity-70 transition-opacity duration-300" aria-hidden="true"></div>
                  
                  <div className="relative h-80 bg-black border border-purple-500/30 rounded-xl overflow-hidden">
                    {/* Background image */}
                    <div className="absolute inset-0">
                      <img 
                        src={item.image} 
                        alt={`${item.title} - UI/UX design portfolio showcasing ${item.description}`}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 opacity-70"
                        loading="lazy"
                        decoding="async"
                        width="400"
                        height="300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent"></div>
                    </div>
                    
                    {/* Animated circuit lines on hover */}
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-30 transition-opacity duration-300" aria-hidden="true">
                      <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                        <pattern id={`circuit-${i}`} patternUnits="userSpaceOnUse" width="20" height="20">
                          <path d="M10,0 L10,10 M0,10 L10,10" stroke="url(#grad)" strokeWidth="0.5" fill="none" />
                        </pattern>
                        <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#8B5CF6" />
                          <stop offset="100%" stopColor="#EC4899" />
                        </linearGradient>
                        <rect x="0" y="0" width="100%" height="100%" fill={`url(#circuit-${i})`} />
                      </svg>
                    </div>
                    
                    {/* Light bulb that glows on hover */}
                    <motion.div 
                      className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      animate={{ y: [0, -5, 0] }}
                      transition={{ repeat: Infinity, duration: 2 }}
                      aria-hidden="true"
                    >
                      <FaLightbulb className="text-2xl text-yellow-300" />
                      <motion.div 
                        className="absolute -inset-3 rounded-full blur-lg z-0"
                        animate={{ opacity: [0.3, 0.7, 0.3], scale: [1, 1.2, 1] }}
                        transition={{ repeat: Infinity, duration: 2 }}
                        style={{
                          background: 'radial-gradient(circle, rgba(253, 224, 71, 0.6) 0%, rgba(253, 224, 71, 0) 70%)',
                        }}
                      />
                    </motion.div>
                    
                    {/* Content */}
                    <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                      <h3 className="text-2xl font-bold mb-2 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-purple-400 group-hover:to-pink-600 transition-all duration-300">
                        {item.title}
                      </h3>
                      
                      <p className="text-gray-300 mb-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        {item.description}
                      </p>
                      
                      {/* Animated line */}
                      <motion.div 
                        className="h-0.5 bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-500"
                        initial={{ width: 0 }}
                        whileInView={{ width: '40%' }}
                        viewport={{ once: true }}
                        whileHover={{ width: '100%' }}
                        aria-hidden="true"
                      />

                      {/* View project button */}
                      <motion.button
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3 + i * 0.1 }}
                        className="mt-4 flex items-center text-sm font-medium text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 group/btn"
                        aria-label={`View ${item.title} project details`}
                      >
                        <span>View Project</span>
                        <FaArrowRight className="ml-2 transform group-hover/btn:translate-x-1 transition-transform duration-300" aria-hidden="true" />
                      </motion.button>
                    </div>
                    
                    {/* Animated corners */}
                    <div className="absolute top-0 left-0 w-10 h-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" aria-hidden="true">
                      <div className="absolute top-0 left-0 w-px h-0 bg-gradient-to-b from-purple-500 to-pink-500 group-hover:h-10 transition-all duration-500 delay-100"></div>
                      <div className="absolute top-0 left-0 h-px w-0 bg-gradient-to-r from-purple-500 to-pink-500 group-hover:w-10 transition-all duration-500 delay-100"></div>
                    </div>
                    <div className="absolute top-0 right-0 w-10 h-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" aria-hidden="true">
                      <div className="absolute top-0 right-0 w-px h-0 bg-gradient-to-b from-purple-500 to-pink-500 group-hover:h-10 transition-all duration-500 delay-200"></div>
                      <div className="absolute top-0 right-0 h-px w-0 bg-gradient-to-l from-purple-500 to-pink-500 group-hover:w-10 transition-all duration-500 delay-200"></div>
                    </div>
                    <div className="absolute bottom-0 left-0 w-10 h-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" aria-hidden="true">
                      <div className="absolute bottom-0 left-0 w-px h-0 bg-gradient-to-t from-purple-500 to-pink-500 group-hover:h-10 transition-all duration-500 delay-300"></div>
                      <div className="absolute bottom-0 left-0 h-px w-0 bg-gradient-to-r from-purple-500 to-pink-500 group-hover:w-10 transition-all duration-500 delay-300"></div>
                    </div>
                    <div className="absolute bottom-0 right-0 w-10 h-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" aria-hidden="true">
                      <div className="absolute bottom-0 right-0 w-px h-0 bg-gradient-to-t from-purple-500 to-pink-500 group-hover:h-10 transition-all duration-500 delay-400"></div>
                      <div className="absolute bottom-0 right-0 h-px w-0 bg-gradient-to-l from-purple-500 to-pink-500 group-hover:w-10 transition-all duration-500 delay-400"></div>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
            
            {/* Design process visualization with circles and lines */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="mt-32 relative"
              aria-hidden="true"
            >
              <div className="absolute -inset-10 bg-gradient-radial from-purple-900/20 to-transparent rounded-full blur-3xl"></div>
            </motion.div>
          </div>
        </section>

        {/* Features section with card design */}
        <section aria-labelledby="features-heading" className="py-20 relative overflow-hidden">
          <div className="absolute inset-0 bg-black"></div>
          
          {/* Background vector elements */}
          <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
            {/* Diagonal lines */}
            {[...Array(10)].map((_, i) => (
              <motion.div
                key={`diagonal-${i}`}
                className="absolute bg-gradient-to-r from-purple-500/0 via-purple-500/20 to-purple-500/0"
                style={{
                  height: '1px',
                  width: '200%',
                  top: `${Math.random() * 100}%`,
                  left: '-50%',
                  transform: `rotate(${Math.random() * 180}deg)`,
                }}
                animate={{
                  opacity: [0.1, 0.3, 0.1]
                }}
                transition={{
                  duration: Math.random() * 10 + 10,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
            ))}
            
            {/* Circular elements - further reduced */}
            {[...Array(4)].map((_, i) => (
              <motion.div
                key={`feature-circle-${i}`}
                className="absolute rounded-full border border-purple-500/30"
                style={{
                  width: Math.random() * 200 + 50,
                  height: Math.random() * 200 + 50,
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{
                  scale: [1, 1.1, 1], // Reduced scale range
                  rotate: 360,
                  opacity: [0.1, 0.15, 0.1] // Reduced opacity range
                }}
                transition={{
                  duration: Math.random() * 25 + 25, // Much slower animation
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
            ))}
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <motion.header
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
              className="text-center mb-8"
            >
              <h2 id="features-heading" className="text-4xl md:text-5xl font-bold mb-6">
                Why Choose Our UI/UX Design Services
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                We deliver exceptional UI/UX design solutions that drive measurable business results and user satisfaction.
              </p>
            </motion.header>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, i) => (
                <motion.article
                  key={`feature-${i}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  whileHover={{ y: -10 }}
                  className="relative group"
                >
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl blur opacity-0 group-hover:opacity-70 transition duration-300" aria-hidden="true"></div>
                  <div className="relative bg-black border border-purple-500/30 rounded-xl p-8 h-full flex flex-col">
                    {/* Icon with neon glow */}
                    <div className="mb-5">
                      <motion.div 
                        className="w-16 h-16 rounded-full bg-black border border-purple-500 flex items-center justify-center"
                        whileHover={{ scale: 1.1 }}
                        animate={{ 
                          boxShadow: ['0 0 0px rgba(139, 92, 246, 0.3)', '0 0 15px rgba(139, 92, 246, 0.7)', '0 0 0px rgba(139, 92, 246, 0.3)']
                        }}
                        transition={{ 
                          boxShadow: { repeat: Infinity, duration: 2 },
                        }}
                        aria-hidden="true"
                      >
                        <div className="text-2xl text-purple-500">{feature.icon}</div>
                      </motion.div>
                    </div>
                    
                    <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                    <p className="text-gray-400 flex-grow">{feature.description}</p>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section aria-labelledby="faq-heading" className="py-20 relative overflow-hidden">
          <div className="absolute inset-0 bg-black"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <motion.header
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
              className="text-center mb-16"
            >
              <h2 id="faq-heading" className="text-4xl md:text-5xl font-bold mb-6">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Get answers to common questions about our UI/UX design services and process.
              </p>
            </motion.header>
            
            <div className="max-w-4xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <motion.details 
                  key={index}
                  className="bg-black border border-purple-500/30 rounded-xl p-6 group"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <summary className="text-xl font-bold cursor-pointer hover:text-purple-400 transition-colors list-none">
                    <div className="flex items-center justify-between">
                      <span>{faq.question}</span>
                      <motion.div
                        className="ml-4"
                        whileHover={{ rotate: 180 }}
                        transition={{ duration: 0.3 }}
                        aria-hidden="true"
                      >
                        <FaArrowRight className="text-purple-500" />
                      </motion.div>
                    </div>
                  </summary>
                  <p className="mt-4 text-gray-300 leading-relaxed">
                    {faq.answer}
                  </p>
                </motion.details>
              ))}
            </div>
          </div>
        </section>

        {/* Call to action with neon effect */}
        <section aria-labelledby="cta-heading" className="py-10 relative">
          <div className="absolute inset-0 bg-black"></div>
          
          {/* Vector elements */}
          <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
            {/* Circular elements */}
            {[...Array(5)].map((_, i) => (
              <motion.div
                key={`cta-circle-${i}`}
                className="absolute rounded-full border border-purple-500/20"
                style={{
                  width: Math.random() * 300 + 100,
                  height: Math.random() * 300 + 100,
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{
                  scale: [1, 1.1, 1],
                  rotate: 360,
                  opacity: [0.1, 0.2, 0.1]
                }}
                transition={{
                  duration: Math.random() * 20 + 20,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
            ))}
            
            {/* Grid pattern */}
            <div className="absolute inset-0 opacity-10">
              <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <defs>
                  <pattern id="smallGrid" width="20" height="20" patternUnits="userSpaceOnUse">
                    <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(139, 92, 246, 0.3)" strokeWidth="0.5"/>
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#smallGrid)" />
              </svg>
            </div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
              className="max-w-4xl mx-auto"
            >
              <div className="relative">
                {/* Neon border glow */}
                <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-pink-600 rounded-3xl blur-xl opacity-70" aria-hidden="true"></div>
                
                <div className="relative bg-black border border-purple-500/50 rounded-3xl p-12 text-center">
                  <motion.div 
                    className="mb-8 relative inline-block"
                    whileInView={{ 
                      scale: [0.9, 1.1, 1],
                      rotate: [0, 5, 0, -5, 0]
                    }}
                    transition={{ duration: 2 }}
                    aria-hidden="true"
                  >
                    <FaLightbulb className="text-6xl text-yellow-300 mx-auto" />
                    <motion.div 
                      className="absolute -inset-8 rounded-full blur-xl z-0"
                      animate={{ 
                        scale: [1, 1.3, 1],
                        opacity: [0.3, 0.7, 0.3]
                      }}
                      transition={{ 
                        repeat: Infinity,
                        duration: 3
                      }}
                      style={{
                        background: 'radial-gradient(circle, rgba(253, 224, 71, 0.5) 0%, rgba(253, 224, 71, 0) 70%)',
                      }}
                    />
                  </motion.div>
                  
                  <h2 id="cta-heading" className="text-4xl md:text-2xl font-bold mb-6">
                    Ready to Transform Your Digital Experience?
                  </h2>
                  <p className="text-xl text-gray-300 mb-10 max-w-3xl mx-auto">
                    Contact us today for expert UI/UX design solutions that enhance usability, aesthetics, and drive business conversions.
                  </p>
                  
                  {/* Decorative vector elements */}
                  {[...Array(4)].map((_, i) => (
                    <motion.div
                      key={`cta-vector-${i}`}
                      className="absolute"
                      style={{
                        top: `${20 + i * 20}%`,
                        left: i % 2 === 0 ? '10%' : '90%',
                      }}
                      animate={{
                        rotate: 360,
                        x: [0, i % 2 === 0 ? 20 : -20, 0],
                        y: [0, 10, 0],
                      }}
                      transition={{
                        rotate: { duration: 20, repeat: Infinity, ease: "linear" },
                        x: { duration: 5, repeat: Infinity, ease: "easeInOut" },
                        y: { duration: 3, repeat: Infinity, ease: "easeInOut" },
                      }}
                      aria-hidden="true"
                    >
                      <svg width="30" height="30" viewBox="0 0 100 100" aria-hidden="true">
                        <motion.path
                          d="M50,10 L90,50 L50,90 L10,50 Z"
                          stroke="rgba(139, 92, 246, 0.6)"
                          strokeWidth="2"
                          fill="none"
                          animate={{
                            strokeDasharray: ["0, 500", "500, 0", "0, 500"],
                          }}
                          transition={{
                            duration: 10,
                            repeat: Infinity,
                            ease: "linear"
                          }}
                        />
                      </svg>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
};

export default TechEcoSolutions;
